import { isFreeShipping } from '../../config/r2rListingBadges';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatListingCardMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { richText } from '../../util/richText';
import { shippingFirstItemRange } from '../../util/shippingRates';
import { isBookingProcessAlias } from '../../transactions/transaction';

const { Money } = sdkTypes;

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const findCategoryNameInTree = (categories, id) => {
  if (!id || !categories?.length) {
    return null;
  }
  for (const category of categories) {
    if (category.id === id) {
      return category.name || null;
    }
    const nestedName = findCategoryNameInTree(category.subcategories, id);
    if (nestedName) {
      return nestedName;
    }
  }
  return null;
};

const getCategoryName = (publicData, config) => {
  const categories = config.categoryConfiguration?.categories || [];
  const { categoryLevel1, categoryLevel2, categoryLevel3 } = publicData || {};
  const categoryId = categoryLevel3 || categoryLevel2 || categoryLevel1;
  return findCategoryNameInTree(categories, categoryId);
};

/**
 * Category eyebrow for listing cards (e.g. Fish, Coral).
 * Uses the deepest category level present on the listing.
 *
 * @param {Object} publicData - listing publicData
 * @param {Object} config - app configuration
 * @returns {string|null}
 */
export const getCategoryEyebrow = (publicData, config) => {
  return getCategoryName(publicData, config);
};

/**
 * Muted first-item range for listing cards when default and zone fees differ.
 * Returns null when shipping is off, always-free, or min === max.
 *
 * @param {Object|null|undefined} publicData
 * @param {string|null|undefined} currency
 * @param {Object} intl
 * @returns {string|null}
 */
export const getListingCardShippingRange = (publicData, currency, intl) => {
  if (publicData?.shippingEnabled !== true || isFreeShipping(publicData) || !currency) {
    return null;
  }
  const { min, max } = shippingFirstItemRange(publicData);
  if (!(min < max)) {
    return null;
  }
  try {
    const minMoney = formatListingCardMoney(intl, new Money(min, currency));
    const maxMoney = formatListingCardMoney(intl, new Money(max, currency));
    return intl.formatMessage(
      { id: 'ListingCard.shippingRange' },
      { min: minMoney, max: maxMoney }
    );
  } catch (e) {
    return null;
  }
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatListingCardMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 *   - showRating: whether the author has public reviews (when false, shows outline star + (0))
 *   - ratingAverage: numeric average rating (or null)
 *   - ratingAverageFormatted: localized average rating string with 1 decimal (or null)
 *   - ratingCount: number of public reviews for the author
 *   - ratingCountLabel: localized "(count)" string for the rating row
 *   - ratingAriaLabel: accessible label when the seller has reviews
 *   - ratingNotYetAriaLabel: accessible label for the not-yet-rated state
 *   - categoryEyebrow: category label for the eyebrow row (or null)
 *   - showCategoryEyebrow: whether to render the category eyebrow
 */
export const getListingCardTranslations = (listing, config, intl, options = {}) => {
  const { showAuthorInfo = true } = options;
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorProfile = listing?.author?.attributes?.profile;
  const authorDisplayName = authorProfile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  // Author review aggregate is maintained on the user's profile metadata by the
  // bridge service (reviewAverage / reviewCount). Rated sellers show a filled star with
  // average + count; unrated sellers show an outline star with (0).
  const reviewAverageRaw = authorProfile?.metadata?.reviewAverage;
  const reviewCountRaw = authorProfile?.metadata?.reviewCount;
  const ratingCount = Number.isFinite(reviewCountRaw) ? reviewCountRaw : 0;
  const ratingAverage = Number.isFinite(reviewAverageRaw) ? reviewAverageRaw : null;
  const showRating = ratingCount > 0 && ratingAverage != null;
  const ratingAverageFormatted = showRating
    ? intl.formatNumber(ratingAverage, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : null;
  const ratingCountLabel = intl.formatMessage(
    { id: 'ListingCard.ratingCount' },
    { count: ratingCount }
  );
  const ratingNotYetAriaLabel = intl.formatMessage({ id: 'ListingCard.ratingNotYetAriaLabel' });
  const ratingAriaLabel = showRating
    ? intl.formatMessage(
        { id: 'ListingCard.ratingAriaLabel' },
        { average: ratingAverageFormatted, count: ratingCount }
      )
    : null;

  const categoryEyebrow = getCategoryEyebrow(publicData, config);
  const showCategoryEyebrow = Boolean(categoryEyebrow);

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Single formatted price line (amount + per-unit if applicable); used for both card aria and price block
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const baseAriaLabel =
    priceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessage }
        )
      : title;
  const sellerRatingAriaLabel = showAuthorInfo
    ? showRating
      ? ratingAriaLabel
      : ratingNotYetAriaLabel
    : null;
  const cardAriaLabel = sellerRatingAriaLabel
    ? `${baseAriaLabel}, ${sellerRatingAriaLabel}`
    : baseAriaLabel;

  const shippingRange = getListingCardShippingRange(publicData, price?.currency, intl);

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showRating,
    ratingAverage,
    ratingAverageFormatted,
    ratingCount,
    ratingCountLabel,
    ratingAriaLabel,
    ratingNotYetAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    categoryEyebrow,
    showCategoryEyebrow,
    shippingRange,
  };
};
